import { IconButton } from '@material-ui/core';
import { FC, useEffect } from 'react';

import { useOptions } from '@agency/contexts/OptionsContext';
import { calculateLeaseEndDate } from '@agency/modules/leases/pages/letting-manage/pages/letting-transaction-details/sections/HeadlineDetailsSection/HeadlineDetailsSection.utility';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { TransactionLeaseEventItem } from '@shared/apis/transactionsLettingsApi.types';
import { DatePicker } from '@shared/components/date-picker';
import { NumberField } from '@shared/components/number-field';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { TextField } from '@shared/components/text-field';
import { t } from '@shared/i18n';
import { CloseIcon } from '@shared/icons/close';
import { ButtonSize } from '@shared/types/common/button';
import { InputSize } from '@shared/types/common/input';

import { styles } from './DisposalRentEventCard.styles';

interface DisposalRentEventCardProps extends WithStyles<typeof styles> {
  event: TransactionLeaseEventItem;
  error: boolean;
  onUpdate: (event: TransactionLeaseEventItem) => void;
  onDelete: (id: string | number) => void;
}

const DisposalRentEventCardComponent: FC<DisposalRentEventCardProps> = ({
  classes,
  event,
  error,
  onUpdate,
  onDelete,
}) => {
  const { reminderDaysOptions } = useOptions();

  const handleUpdate = (payload: Partial<TransactionLeaseEventItem>) => {
    onUpdate({ ...event, ...payload });
  };

  useEffect(() => {
    if (event.date_priority === true && event.from && (event.date_years || event.date_months || event.date_days)) {
      const calculatedEndDate = calculateLeaseEndDate({
        lease_start: event.from,
        lease_length_years: event.date_years ?? undefined,
        lease_length_months: event.date_months ?? undefined,
        lease_length_days: event.date_days ?? undefined,
      });

      if (calculatedEndDate && event.to !== calculatedEndDate) {
        handleUpdate({ to: calculatedEndDate });
      }
    }
  }, [event.from, event.date_years, event.date_months, event.date_days, event.date_priority]);

  return (
    <div className={classes.root}>
      <IconButton onClick={() => onDelete(event.id)} className={classes.deleteButton}>
        <CloseIcon />
      </IconButton>
      <div className={classes.cardContent}>
        <div className={classes.row}>
          <div className={classes.field}>
            <span className={classes.label}>{t('start_date_1')}</span>
            <DatePicker
              placeholder="DD/MM/YYYY"
              size={InputSize.medium}
              value={event.from}
              fullWidth
              onChange={(value) =>
                handleUpdate({
                  from: value,
                  ...(!value && {
                    to: null,
                    date_years: null,
                    date_months: null,
                    date_days: null,
                    date_priority: null,
                  }),
                })
              }
            />
          </div>
          <div className={classes.field}>
            <span className={classes.label}>{t('duration')}</span>
            <div className={classes.durationFields}>
              <NumberField
                fullWidth
                min={0}
                disabled={!event.from || event.date_priority === false}
                size={InputSize.medium}
                InputProps={{ endAdornment: t('years') }}
                value={event.date_years ?? ''}
                onChange={(value) =>
                  handleUpdate({
                    date_years: value,
                    date_priority: !!value || !!event.date_months || !!event.date_days ? true : null,
                  })
                }
              />
              <NumberField
                fullWidth
                min={0}
                disabled={!event.from || event.date_priority === false}
                size={InputSize.medium}
                InputProps={{ endAdornment: t('months') }}
                value={event.date_months ?? ''}
                onChange={(value) =>
                  handleUpdate({
                    date_months: value,
                    date_priority: !!value || !!event.date_years || !!event.date_days ? true : null,
                  })
                }
              />
              <NumberField
                fullWidth
                min={0}
                disabled={!event.from || event.date_priority === false}
                size={InputSize.medium}
                InputProps={{ endAdornment: t('days') }}
                value={event.date_days ?? ''}
                onChange={(value) =>
                  handleUpdate({
                    date_days: value,
                    date_priority: !!value || !!event.date_years || !!event.date_months ? true : null,
                  })
                }
              />
            </div>
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.field}>
            <span className={classes.label}>{t('end_date_1')}</span>
            <DatePicker
              placeholder="DD/MM/YYYY"
              size={InputSize.medium}
              value={event.to}
              fullWidth
              disabled={!event.from || event.date_priority === true}
              minDate={event.from}
              onChange={(value) =>
                handleUpdate({
                  to: value,
                  date_priority: !value ? null : false,
                })
              }
            />
          </div>
          <div className={classes.field}>
            <span className={classes.label}>{t('reminder')}</span>
            <SingleSelect
              placeholder={t('xtextx_ellipsis', { text: t('select') })}
              size={ButtonSize.medium}
              value={event.reminder_days ?? -1}
              options={reminderDaysOptions}
              onChange={(value) => handleUpdate({ reminder_days: Number(value) })}
            />
          </div>
          <div className={classes.field}>
            <span className={classes.label}>{t('comments')}</span>
            <TextField
              classes={{ root: classes.textField }}
              multiline
              fullWidth
              minRows={1}
              placeholder={t('comment_here')}
              size={InputSize.medium}
              value={event.comments ?? ''}
              onChange={(e) => handleUpdate({ comments: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const DisposalRentEventCard = withStyles(styles)(DisposalRentEventCardComponent);
